import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Briefcase, Eye, Edit, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function JobApplications() {
  const { user } = useAuth();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedApp, setSelectedApp] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({
    applicant_name: '',
    phone: '',
    position: '',
    cover_letter: '',
    status: 'pending'
  });

  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/job-applications`, {
        withCredentials: true,
      });
      setApplications(data);
    } catch (error) {
      console.error('Error fetching applications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (appId, newStatus) => {
    try {
      await axios.patch(
        `${BACKEND_URL}/api/job-applications/${appId}`,
        { status: newStatus },
        { withCredentials: true }
      );
      toast.success('Application status updated');
      fetchApplications();
      setDialogOpen(false);
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const openViewDialog = (app) => {
    setSelectedApp(app);
    setDialogOpen(true);
  };

  const openEditDialog = (app) => {
    setSelectedApp(app);
    setEditFormData({
      applicant_name: app.applicant_name,
      phone: app.phone,
      position: app.position,
      cover_letter: app.cover_letter,
      status: app.status
    });
    setEditDialogOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(
        `${BACKEND_URL}/api/job-applications/${selectedApp.id}`,
        editFormData,
        { withCredentials: true }
      );
      toast.success('Application updated successfully');
      setEditDialogOpen(false);
      fetchApplications();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update application');
    }
  };

  const handleDelete = async (appId) => {
    if (!window.confirm('Are you sure you want to delete this application? This action cannot be undone.')) {
      return;
    }

    try {
      await axios.delete(
        `${BACKEND_URL}/api/job-applications/${appId}`,
        { withCredentials: true }
      );
      toast.success('Application deleted successfully');
      fetchApplications();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete application');
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div data-testid="job-applications-page">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-[#0F172A] tracking-tight" style={{ fontFamily: 'Manrope' }}>
            Job Applications
          </h1>
          <p className="text-[#64748B] mt-2">Manage teaching position applications</p>
        </div>
        <a
          href="/apply"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center px-6 py-3 bg-[#0F172A] hover:bg-[#1E293B] text-white rounded-md font-medium transition-colors"
          data-testid="public-application-link"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Public Application Form
        </a>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Applications</CardTitle>
          <CardDescription>All received job applications</CardDescription>
        </CardHeader>
        <CardContent>
          {applications.length === 0 ? (
            <div className="text-center py-12">
              <Briefcase className="w-12 h-12 text-[#64748B] mx-auto mb-4" />
              <p className="text-[#64748B]">No job applications found</p>
            </div>
          ) : (
            <div className="border rounded-md">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date Applied</TableHead>
                    <TableHead>Applicant Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Position</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {applications.map((app) => (
                    <TableRow key={app.id} className="table-row" data-testid="application-row">
                      <TableCell className="font-medium">
                        {new Date(app.applied_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>{app.applicant_name}</TableCell>
                      <TableCell>{app.email}</TableCell>
                      <TableCell>{app.phone}</TableCell>
                      <TableCell>{app.position}</TableCell>
                      <TableCell>
                        <span
                          className={`badge badge-${app.status}`}
                          data-testid={`status-${app.status}`}
                        >
                          {app.status}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button
                            data-testid="view-application-btn"
                            size="sm"
                            variant="outline"
                            onClick={() => openViewDialog(app)}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View
                          </Button>
                          <Button
                            data-testid="edit-application-btn"
                            size="sm"
                            variant="outline"
                            onClick={() => openEditDialog(app)}
                          >
                            <Edit className="w-4 h-4 mr-1" />
                            Edit
                          </Button>
                          {(user.role === 'admin' || user.role === 'board_member') && (
                            <Button
                              data-testid="delete-application-btn"
                              size="sm"
                              variant="outline"
                              onClick={() => handleDelete(app.id)}
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* View Dialog */}
      {selectedApp && (
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Application Details</DialogTitle>
              <DialogDescription>Review and update application status</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-[#64748B]">Applicant Name</p>
                  <p className="text-[#0F172A] font-semibold">{selectedApp.applicant_name}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-[#64748B]">Position</p>
                  <p className="text-[#0F172A] font-semibold">{selectedApp.position}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-[#64748B]">Email</p>
                  <p className="text-[#0F172A]">{selectedApp.email}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-[#64748B]">Phone</p>
                  <p className="text-[#0F172A]">{selectedApp.phone}</p>
                </div>
              </div>
              {selectedApp.resume_url && (
                <div>
                  <p className="text-sm font-medium text-[#64748B] mb-2">Resume</p>
                  <a
                    href={`${BACKEND_URL}${selectedApp.resume_url}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-4 py-2 bg-[#F1F5F9] hover:bg-[#E2E8F0] border border-[#E2E8F0] rounded-md text-sm font-medium text-[#0F172A] transition-colors"
                    data-testid="download-resume-btn"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Download {selectedApp.resume_filename || 'Resume'}
                  </a>
                </div>
              )}
              <div>
                <p className="text-sm font-medium text-[#64748B] mb-2">Cover Letter</p>
                <div className="bg-[#F9FAFB] border border-[#E2E8F0] rounded-md p-4">
                  <p className="text-sm text-[#0F172A]">{selectedApp.cover_letter}</p>
                </div>
              </div>
              <div>
                <p className="text-sm font-medium text-[#64748B] mb-2">Update Status</p>
                <Select
                  defaultValue={selectedApp.status}
                  onValueChange={(value) => handleStatusUpdate(selectedApp.id, value)}
                >
                  <SelectTrigger data-testid="update-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="reviewing">Reviewing</SelectItem>
                    <SelectItem value="shortlisted">Shortlisted</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {selectedApp.reviewed_by && (
                <div>
                  <p className="text-sm text-[#64748B]">
                    Reviewed by: <span className="font-medium">{selectedApp.reviewed_by}</span>
                  </p>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Edit Dialog */}
      {selectedApp && (
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Edit Application</DialogTitle>
              <DialogDescription>Update application details</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleEditSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-name">Applicant Name</Label>
                  <Input
                    id="edit-name"
                    data-testid="edit-name-input"
                    value={editFormData.applicant_name}
                    onChange={(e) => setEditFormData({ ...editFormData, applicant_name: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-phone">Phone</Label>
                  <Input
                    id="edit-phone"
                    data-testid="edit-phone-input"
                    value={editFormData.phone}
                    onChange={(e) => setEditFormData({ ...editFormData, phone: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-position">Position</Label>
                <Input
                  id="edit-position"
                  data-testid="edit-position-input"
                  value={editFormData.position}
                  onChange={(e) => setEditFormData({ ...editFormData, position: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-cover-letter">Cover Letter</Label>
                <Textarea
                  id="edit-cover-letter"
                  data-testid="edit-cover-letter-input"
                  value={editFormData.cover_letter}
                  onChange={(e) => setEditFormData({ ...editFormData, cover_letter: e.target.value })}
                  required
                  rows={6}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-status">Status</Label>
                <Select
                  value={editFormData.status}
                  onValueChange={(value) => setEditFormData({ ...editFormData, status: value })}
                >
                  <SelectTrigger data-testid="edit-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="reviewing">Reviewing</SelectItem>
                    <SelectItem value="shortlisted">Shortlisted</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" data-testid="save-edit-btn" className="bg-[#0F172A] hover:bg-[#1E293B]">
                  Save Changes
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
